import { ApiError } from '../api/errors';
import { backoffDelay } from './outbox';
import { isTransient, isUnreachable } from './push';
import type { PhotoRecord } from './records';
import { isProblem, type SyncApi, type SyncRequestOptions } from './sync-api';
import type { TenantDatabase } from './tenant-db';
import { problemSchema, type WireProblem } from './wire';

/**
 * Foto's gaan níét in een batch maar elk via `POST /media`, en pas als hun
 * eigenaar op de server staat: een foto koppelt aan `mediable_uuid =
 * answer_uuid` (of `finding_uuid` bij een handmatige bevinding), en dat
 * antwoord of die bevinding moet de server dus kennen.
 *
 * Het antwoord wordt pas bij de upload opgezocht, op (vraag, bereik). Nam de
 * server bij een conflict een ander `answer_uuid` over, dan gaat de foto
 * gewoon naar dát antwoord.
 */

export interface PhotoTarget {
    type: 'inspection_answer' | 'finding';
    uuid: string;
}

export interface DuePhoto {
    photo: PhotoRecord;
    target: PhotoTarget;
}

/**
 * De foto's die nu geüpload mogen worden, in de volgorde waarin ze gemaakt zijn.
 *
 * Ook een verwijderde foto die nog op `uploading` staat: die upload werd
 * onderbroken (tabblad dicht, laptop dichtgeklapt) en niemand weet of de server
 * hem heeft. Opnieuw aanbieden is veilig - de server herkent `client_uuid` - en
 * daarna kan hij gewoon verwijderd worden. Zonder dit bleef hij voor altijd als
 * "nog te versturen" staan.
 */
export async function duePhotoUploads(db: TenantDatabase, now: number): Promise<DuePhoto[]> {
    const candidates = (await db.photos.where('state').anyOf('pending', 'uploading').toArray())
        .filter(
            (photo) =>
                photo.blob !== null &&
                (photo.deleted !== 1 || photo.state === 'uploading') &&
                photo.nextAttemptAt <= now,
        )
        .sort((a, b) => a.createdAt - b.createdAt);
    const due: DuePhoto[] = [];

    for (const photo of candidates) {
        const target = await confirmedTarget(db, photo);

        if (target) {
            due.push({ photo, target });
        }
    }

    return due;
}

/** Waar de foto heen gaat, zodra de server die eigenaar kent. */
async function confirmedTarget(
    db: TenantDatabase,
    photo: PhotoRecord,
): Promise<PhotoTarget | null> {
    if (photo.findingUuid) {
        const finding = await db.findings.get(photo.findingUuid);

        return finding?.confirmed === 1 ? { type: 'finding', uuid: finding.findingUuid } : null;
    }

    const answer = await db.answers
        .where('[inspectionUuid+slot]')
        .equals([photo.inspectionUuid, photo.slot])
        .first();

    return answer?.confirmed === 1 ? { type: 'inspection_answer', uuid: answer.answerUuid } : null;
}

/** `aborted`: de ronde werd afgebroken ("Nu versturen" op een vastgelopen upload) - geen mislukte poging. */
export type PhotoUploadOutcome = 'uploaded' | 'retry' | 'unreachable' | 'rejected' | 'aborted';

/** Het `type` van de melding voor een foto waarvan de bytes op dit apparaat niet meer te lezen zijn. */
export const PHOTO_UNREADABLE = 'photo-unreadable';

export async function uploadPhoto(
    db: TenantDatabase,
    api: Pick<SyncApi, 'uploadMedia'>,
    { photo, target }: DuePhoto,
    now: () => number,
    request: SyncRequestOptions = {},
): Promise<PhotoUploadOutcome> {
    if (!photo.blob) {
        return 'rejected';
    }

    const file = await readStoredPhoto(photo.blob, photo.mimeType);

    if (!file) {
        await refuse(db, photo, now(), {
            type: PHOTO_UNREADABLE,
            title: 'Foto niet te lezen',
            detail: 'Deze foto staat op dit apparaat, maar is niet meer te lezen. Voeg hem opnieuw toe, of sla hem over.',
        });

        return 'rejected';
    }

    await db.photos.update(photo.clientUuid, { state: 'uploading' });

    try {
        const result = await api.uploadMedia(
            {
                clientUuid: photo.clientUuid,
                file,
                fileName: photo.fileName,
                mediableType: target.type,
                mediableUuid: target.uuid,
                inspectionUuid: photo.inspectionUuid,
                caption: photo.caption,
                takenAt: photo.takenAt,
            },
            request,
        );

        await db.photos.update(photo.clientUuid, {
            state: 'uploaded',
            answerUuid: target.type === 'inspection_answer' ? target.uuid : null,
            urls: result.urls,
            mediaUuid: result.mediaUuid,
            attempts: 0,
            nextAttemptAt: 0,
            lastError: null,
        });

        return 'uploaded';
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);

        /* Afgebroken door de engine zelf: meteen weer aan de beurt, zonder strafpunt. */
        if (request.signal?.aborted) {
            await db.photos.update(photo.clientUuid, { state: 'pending' });

            return 'aborted';
        }

        /* `422 media-target-unresolvable`: het antwoord is er (nog) niet - later opnieuw. */
        if (isProblem(error, 'media-target-unresolvable') || isTransient(error)) {
            const attempts = photo.attempts + 1;
            const retryAfter = error instanceof ApiError ? error.retryAfterMs : undefined;

            await db.photos.update(photo.clientUuid, {
                state: 'pending',
                attempts,
                nextAttemptAt: now() + (retryAfter ?? backoffDelay(attempts)),
                lastError: message,
            });

            return isUnreachable(error) ? 'unreachable' : 'retry';
        }

        /* Type, grootte of inhoud geweigerd: zichtbaar in de conflictenlijst. */
        const problem = error instanceof ApiError ? problemSchema.parse(error.problem) : null;

        await refuse(db, photo, now(), problem, message);

        return 'rejected';
    }
}

/**
 * De bytes van een opgeslagen foto, vers in het geheugen.
 *
 * Een Blob uit IndexedDB verwijst naar een bestand van de browser. Safari raakt
 * die verwijzing soms kwijt; een upload faalt dan met dezelfde "Load failed" als
 * een wegvallende verbinding en werd eindeloos opnieuw geprobeerd, zonder dat
 * iemand het zag. Eerst lezen maakt van die fout een duidelijke melding, en een
 * kopie in het geheugen gaat wél over de lijn.
 */
async function readStoredPhoto(blob: Blob, mimeType: string): Promise<Blob | null> {
    try {
        const bytes = await blob.arrayBuffer();

        return bytes.byteLength > 0 ? new Blob([bytes], { type: mimeType || blob.type }) : null;
    } catch {
        return null;
    }
}

/** De foto staat op `failed` en de reden in de conflictenlijst, met "opnieuw proberen" of "overslaan". */
async function refuse(
    db: TenantDatabase,
    photo: PhotoRecord,
    at: number,
    problem: WireProblem | null,
    message?: string,
): Promise<void> {
    await db.transaction('rw', db.photos, db.conflicts, async () => {
        await db.photos.update(photo.clientUuid, {
            state: 'failed',
            attempts: photo.attempts + 1,
            lastError: message ?? problem?.detail ?? problem?.title ?? null,
        });
        await db.conflicts.put({
            id: photo.clientUuid,
            kind: 'photo',
            entity: 'media',
            entityKey: `photo:${photo.clientUuid}`,
            inspectionUuid: photo.inspectionUuid,
            localPayload: {
                client_uuid: photo.clientUuid,
                file_name: photo.fileName,
                mime_type: photo.mimeType,
                byte_size: photo.byteSize,
            },
            serverState: null,
            problem,
            clientUpdatedAt: photo.takenAt,
            detectedAt: at,
            acknowledged: 0,
        });
    });
}

/**
 * Een foto nog een keer aanbieden: een die de server weigerde, of een die door
 * een netwerkfout in de backoff staat. De bytes staan nog op het apparaat, dus
 * dit kost de inspecteur geen nieuwe foto; de sync-engine pakt hem op in de
 * eerstvolgende ronde, zonder op de backoff te wachten.
 *
 * Alleen Dexie-werk, zodat dit binnen een `applyMutation()`-transactie past.
 */
export async function retryPhotoUpload(db: TenantDatabase, photo: PhotoRecord): Promise<boolean> {
    if (photo.state === 'uploaded' || photo.blob === null) {
        return false;
    }

    await db.photos.update(photo.clientUuid, {
        state: 'pending',
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
    });

    return true;
}

/**
 * Bestanden die bij het toevoegen alsnog niet te lezen bleken - een foto die
 * alleen in iCloud staat, of een kaart die eruit ging. Ze worden nooit stil
 * overgeslagen: dezelfde melding als een weigering door de server, boven de
 * vragen.
 */
export async function recordUnreadablePhotos(
    db: TenantDatabase,
    inspectionUuid: string,
    fileNames: readonly string[],
    now: number,
): Promise<void> {
    if (fileNames.length === 0) {
        return;
    }

    await db.conflicts.bulkPut(
        fileNames.map((fileName) => ({
            id: `photo-unreadable:${inspectionUuid}:${fileName}`,
            kind: 'photo' as const,
            entity: 'media' as const,
            entityKey: `photo:${fileName}`,
            inspectionUuid,
            localPayload: { file_name: fileName },
            serverState: null,
            problem: {
                type: 'photo-unreadable',
                title: 'Foto niet toegevoegd',
                detail: 'Dit bestand kon niet van dit apparaat gelezen worden. Maak de foto opnieuw en voeg hem opnieuw toe.',
            },
            clientUpdatedAt: null,
            detectedAt: now,
            acknowledged: 0 as const,
        })),
    );
}

/** Foto's die nog naar de server moeten (ook als ze op hun antwoord wachten) of er nog af moeten. */
export async function pendingPhotoCount(db: TenantDatabase): Promise<number> {
    return db.photos
        .filter(
            (photo) =>
                photo.deleted === 1 || photo.state === 'pending' || photo.state === 'uploading',
        )
        .count();
}

/**
 * De foto's die de inspecteur verwijderde en die op de server staan. Een foto
 * die nog aan het uploaden is, wacht tot hij er is.
 */
export async function duePhotoDeletions(db: TenantDatabase, now: number): Promise<PhotoRecord[]> {
    return (await db.photos.where('state').equals('uploaded').toArray()).filter(
        (photo) => photo.deleted === 1 && photo.nextAttemptAt <= now,
    );
}

export type PhotoDeleteOutcome = 'deleted' | 'retry' | 'unreachable' | 'refused' | 'aborted';

/**
 * `DELETE /media/{uuid}`. Weigert de server (de foto zit in een verstuurd
 * rapport, of je mag hem niet verwijderen), dan komt hij terug in de strip en
 * staat de reden in de conflictenlijst.
 */
export async function deletePhoto(
    db: TenantDatabase,
    api: Pick<SyncApi, 'deleteMedia'>,
    photo: PhotoRecord,
    now: () => number,
    request: SyncRequestOptions = {},
): Promise<PhotoDeleteOutcome> {
    try {
        if (!photo.mediaUuid) {
            throw new PhotoWithoutMediaError();
        }

        await api.deleteMedia(photo.mediaUuid, request);
        await db.photos.delete(photo.clientUuid);

        return 'deleted';
    } catch (error) {
        if (request.signal?.aborted) {
            return 'aborted';
        }

        if (!(error instanceof PhotoWithoutMediaError) && isTransient(error)) {
            const attempts = photo.attempts + 1;
            const retryAfter = error instanceof ApiError ? error.retryAfterMs : undefined;

            await db.photos.update(photo.clientUuid, {
                attempts,
                nextAttemptAt: now() + (retryAfter ?? backoffDelay(attempts)),
                lastError: error instanceof Error ? error.message : String(error),
            });

            return isUnreachable(error) ? 'unreachable' : 'retry';
        }

        const problem = error instanceof ApiError ? problemSchema.parse(error.problem) : null;

        await db.transaction('rw', db.photos, db.conflicts, async () => {
            await db.photos.update(photo.clientUuid, {
                deleted: 0,
                attempts: 0,
                nextAttemptAt: 0,
                lastError: null,
            });
            await db.conflicts.put({
                id: `${photo.clientUuid}:delete`,
                kind: 'photo',
                entity: 'media',
                entityKey: `photo:${photo.clientUuid}`,
                inspectionUuid: photo.inspectionUuid,
                localPayload: {
                    action: 'delete',
                    client_uuid: photo.clientUuid,
                    file_name: photo.fileName,
                },
                serverState: null,
                problem,
                clientUpdatedAt: null,
                detectedAt: now(),
                acknowledged: 0,
            });
        });

        return 'refused';
    }
}

class PhotoWithoutMediaError extends Error {
    constructor() {
        super('Deze foto is niet vanaf dit apparaat geüpload en kan hier niet verwijderd worden.');
        this.name = 'PhotoWithoutMediaError';
    }
}
